import React from 'react';
import {Editor, Transforms} from 'slate';
import {
  ReactEditor,
  type RenderElementProps,
  useReadOnly,
  useSelected,
  useSlateStatic,
} from 'slate-react';

import {useSelector} from '../../../state/hooks';
import {useUploadMarkdownImage} from '../../../util/images';
import {useResizer} from '../../../util/resize';
import {WBSlateReduxBridgeContext} from '../WBSlateReduxBridgeContext';
import {BlockWrapper, useDrag} from './drag-drop';
import * as S from './images.styles';
import {type Image} from './images-utils';

export const ImageElement: React.FC<RenderElementProps & {element: Image}> = ({
  attributes,
  element,
  children,
}) => {
  const selected = useSelected();
  const editor = useSlateStatic();
  const readOnly = useReadOnly();

  const {viewId, viewRef} = React.useContext(WBSlateReduxBridgeContext);
  const view = useSelector(state =>
    viewRef != null ? state.views.views[viewRef.id] : null
  );
  const publicAccess = view?.project == null || !view.project.readOnly;

  const {uploadImages, uploadState, setUploadState} = useUploadMarkdownImage(
    viewId,
    publicAccess
  );

  const [draggingImageOver, setDraggingImageOver] = React.useState(false);

  React.useEffect(() => {
    if (uploadState == null) {
      return;
    }

    const {url} = uploadState;

    if (url == null || element.url === url) {
      return;
    }

    Transforms.setNodes(
      editor,
      {url, file: undefined},
      {at: ReactEditor.findPath(editor, element)}
    );
  }, [editor, element, uploadState]);

  // from drag and drop or paste
  const {file} = element;
  React.useEffect(() => {
    if (file == null) {
      return;
    }

    uploadImages([file as File]);
  }, [file, uploadImages]);

  const {sourceAttributes, handleAttributes} = useDrag(element, {
    onHandleMouseDown() {
      if (readOnly) {
        return;
      }

      Transforms.select(editor, ReactEditor.findPath(editor, element));
    },
  });

  const {size, setSize, dropSize, cursor, onMouseDown, resizing} = useResizer(
    'right',
    element.width || 0,
    {deltaMultiplier: 2, min: 24}
  );

  const imageRef = React.useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    if (size === 0) {
      return;
    }

    Transforms.setNodes(
      editor,
      {width: size},
      {at: ReactEditor.findPath(editor, element)}
    );
  }, [size, element, editor]);

  if (
    element.url == null ||
    uploadState?.uploading ||
    uploadState?.error != null
  ) {
    return (
      <BlockWrapper attributes={attributes} element={element}>
        <div contentEditable={false}>
          <S.ImagePlaceholderWrapper
            selected={selected}
            draggingImageOver={draggingImageOver}>
            <S.ImagePlaceholder>
              <S.ImagePlaceholderIcon />
              <span>
                {uploadState?.uploading
                  ? `Uploading ${uploadState.name}...`
                  : uploadState?.error != null
                    ? `Error: ${uploadState.error}`
                    : 'Drag an image here'}
              </span>
            </S.ImagePlaceholder>
            <S.ImagePlaceholderInput
              type="file"
              accept="image/x-png,image/gif,image/jpeg"
              onDragEnter={e => {
                // Hack for Safari because it doesn't support dataTransfer.items
                if (e.dataTransfer.items.length === 0) {
                  e.stopPropagation();
                  setDraggingImageOver(true);
                }

                for (let i = 0; i < e.dataTransfer.items.length; i++) {
                  if (e.dataTransfer.items[i].type.includes('image')) {
                    e.stopPropagation();
                    setDraggingImageOver(true);
                    break;
                  }
                }
              }}
              onDragLeave={e => {
                if (draggingImageOver) {
                  e.stopPropagation();
                  setDraggingImageOver(false);
                }
              }}
              onDrop={e => {
                if (draggingImageOver) {
                  e.stopPropagation();
                  setDraggingImageOver(false);
                }
              }}
              onChange={e => {
                const {files} = e.target;

                if (files == null) {
                  return;
                }

                uploadImages(Array.from(files));
              }}></S.ImagePlaceholderInput>
          </S.ImagePlaceholderWrapper>
        </div>
        {children}
      </BlockWrapper>
    );
  }

  return (
    <BlockWrapper attributes={attributes} element={element}>
      <S.ImageOuterWrapper>
        <S.ImageWrapper>
          <div contentEditable={false} style={{position: 'relative'}}>
            {(() => {
              const content = (
                <S.Image
                  {...sourceAttributes}
                  {...handleAttributes}
                  selected={selected}
                  src={element.url}
                  ref={imageRef}
                  width={element.width == null ? undefined : dropSize}
                  onDragStart={e => {
                    if (readOnly) {
                      e.stopPropagation();
                    }
                  }}
                  onError={() => {
                    if (!readOnly) {
                      setUploadState({
                        error: "This image couldn't be loaded.",
                        uploading: false,
                        name: 'Failed to load',
                      });
                    }
                  }}
                />
              );

              if (element.href == null) {
                return content;
              } else {
                return (
                  <S.LinkedImageWrapper href={element.href} target="_blank">
                    {content}
                  </S.LinkedImageWrapper>
                );
              }
            })()}
            {!readOnly && (
              <S.ResizeHandleWrapper
                cursor={cursor}
                resizing={resizing}
                onMouseDown={e => {
                  if (imageRef.current != null) {
                    onMouseDown(e);
                    setSize(imageRef.current.clientWidth);
                  }
                }}>
                <S.ResizeHandle />
              </S.ResizeHandleWrapper>
            )}
          </div>
          {element.hasCaption ? (
            <S.ImageCaption
              empty={Editor.isEmpty(editor, element)}
              selected={selected}>
              {children}
            </S.ImageCaption>
          ) : (
            <>
              {!readOnly && selected && (
                <S.AddCaptionButton
                  contentEditable={false}
                  onMouseDown={e => {
                    e.preventDefault();
                    Transforms.setNodes(
                      editor,
                      {hasCaption: true},
                      {at: ReactEditor.findPath(editor, element)}
                    );
                  }}>
                  Write a caption...
                </S.AddCaptionButton>
              )}
              {children}
            </>
          )}
        </S.ImageWrapper>
      </S.ImageOuterWrapper>
    </BlockWrapper>
  );
};
